import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { track, EVENTS } from '@/utils/analytics'
import { X, Plane } from '@/components/icons'
import { Button } from './ui/button'
import { PrntrCard } from './ui/prntr-input'
import {
  NumRow,
  PickerRowField,
  SwitchRowField,
  TextRow,
  TextareaField,
} from './ui/prntr-fields'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from './ui/drawer'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { AirportPickerDrawer } from './AirportPickerDrawer'
import {
  useAirportByIcao,
  useAircraftGetById,
  useAircraftUpdate,
  useAircraftCreate,
  useAircraftUnarchive
} from '../lib/hooks'
import { FacilityPhotos, type FacilityPhotosHandle } from './FacilityPhotos'

const AircraftFormSchema = z.object({
  registration: z.string().min(1, 'Registration is required'),
  type: z.string(),
  baseAirportIcao: z.string().min(1, 'Base airport is required'),
  maxPassengers: z.number().min(0, 'Valid passenger capacity required'),
  crewCapacity: z.number().min(0, 'Valid crew capacity required'),
  isDefault: z.boolean(),
  // Facilities (free-text, optional)
  cateringGalley: z.string().optional(),
  cabinComfort: z.string().optional(),
  entertainmentConnectivity: z.string().optional(),
  safetyEquipment: z.string().optional(),
  storageSupplies: z.string().optional(),
  facilityNotes: z.string().optional(),
  medicalInfo: z.string().optional(),
})

export type AircraftData = z.infer<typeof AircraftFormSchema>

// Free-text facility textareas rendered under the "Preferences" section, in order.
const FACILITY_FIELDS: { name: keyof AircraftData; label: string }[] = [
  { name: 'cateringGalley', label: 'Catering & Galley' },
  { name: 'cabinComfort', label: 'Cabin & Comfort' },
  { name: 'entertainmentConnectivity', label: 'Entertainment & Connectivity' },
  { name: 'safetyEquipment', label: 'Safety & Emergency Equipment' },
  { name: 'storageSupplies', label: 'Storage & Supplies' },
  { name: 'facilityNotes', label: 'Notes' },
  { name: 'medicalInfo', label: 'Medical Information' },
]

interface AircraftDrawerProps {
  isOpen: boolean
  onClose: () => void
  aircraftId?: string // When provided, opens in edit mode
}

const defaultFormData: AircraftData = {
  registration: '',
  type: '',
  baseAirportIcao: '',
  maxPassengers: 0,
  crewCapacity: 0,
  isDefault: false,
  cateringGalley: '',
  cabinComfort: '',
  entertainmentConnectivity: '',
  safetyEquipment: '',
  storageSupplies: '',
  facilityNotes: '',
  medicalInfo: '',
}

export function AircraftDrawer({
  isOpen,
  onClose,
  aircraftId,
}: AircraftDrawerProps) {
  const [isAirportPickerOpen, setIsAirportPickerOpen] = useState(false)
  // Set when a save collides with an archived aircraft; drives the unarchive
  // confirmation dialog.
  const [unarchiveTarget, setUnarchiveTarget] = useState<{
    aircraftId: string
    registration: string
  } | null>(null)

  const { data: aircraftData } = useAircraftGetById(aircraftId)

  const {
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isDirty, isValid },
    reset,
  } = useForm<AircraftData>({
    resolver: zodResolver(AircraftFormSchema),
    defaultValues: defaultFormData,
    mode: 'onChange',
  })

  const watchBaseAirportIcao = watch('baseAirportIcao')
  const airport = useAirportByIcao(watchBaseAirportIcao)
  const { mutateAsync: updateAircraft, isPending: isUpdating } = useAircraftUpdate(aircraftId)
  const { mutateAsync: createAircraft, isPending: isCreating } = useAircraftCreate()
  const { mutate: unarchiveAircraft } = useAircraftUnarchive()
  // The aircraft mutation resolves before staged photos finish uploading, so
  // track the photo-flush phase separately to keep Save in its loading state
  // until every image is uploaded.
  const [isFlushingPhotos, setIsFlushingPhotos] = useState(false)
  const isSaving = isUpdating || isCreating || isFlushingPhotos

  const isEditMode = !!aircraftId

  // Block closing the drawer (outside click, drag, X, Cancel) while a save is in
  // flight so photo uploads aren't interrupted mid-flush.
  const handleRequestClose = () => {
    if (isSaving) return
    onClose()
  }

  // Per-facility photo uploaders. Each manages its own staged additions/removals;
  // we flush them on save (against the saved aircraft's id). Refs are keyed by
  // facility field name. `pendingPhotoCounts` lets a photo-only change enable Save.
  const photoRefs = useRef<Record<string, FacilityPhotosHandle | null>>({})
  const [pendingPhotoCounts, setPendingPhotoCounts] = useState<
    Record<string, number>
  >({})
  const pendingPhotoTotal = Object.values(pendingPhotoCounts).reduce(
    (sum, n) => sum + n,
    0
  )

  // Photos already attached to each facility, grouped from the loaded aircraft.
  const facilityPhotos =
    (aircraftData as
      | { facilityPhotos?: { facility: string; s3Key: string }[] }
      | undefined)?.facilityPhotos ?? []
  const photosByFacility = facilityPhotos.reduce<Record<string, string[]>>(
    (acc, p) => {
      ;(acc[p.facility] ??= []).push(p.s3Key)
      return acc
    },
    {}
  )

  const flushFacilityPhotos = async (resolvedAircraftId: string) => {
    // Photo upload errors are surfaced per-file inside each uploader; swallow here
    // so a photo failure doesn't masquerade as an aircraft-save failure.
    setIsFlushingPhotos(true)
    try {
      await Promise.all(
        Object.values(photoRefs.current).map((r) =>
          r?.flushPending(resolvedAircraftId)
        )
      )
    } catch {
      /* already toasted */
    } finally {
      setIsFlushingPhotos(false)
    }
  }

  // The backend rejects a registration that already belongs to an archived
  // aircraft with a CONFLICT carrying { aircraftId, isArchived: true }. Open a
  // confirmation dialog offering to unarchive it instead of creating/renaming
  // onto it. Returns true when the error was a conflict (handled here), so
  // callers skip their generic error toast.
  const handleConflict = (registration: string, error: unknown): boolean => {
    const err = error as {
      code?: string
      message?: string
      data?: { aircraftId?: string; isArchived?: boolean }
    }
    if (err?.code !== 'CONFLICT') return false

    if (err.data?.isArchived && err.data.aircraftId) {
      setUnarchiveTarget({ aircraftId: err.data.aircraftId, registration })
    } else {
      toast.error(err.message ?? 'An aircraft with this registration already exists.')
    }
    return true
  }

  const handleUnarchiveConfirm = () => {
    if (!unarchiveTarget) return
    unarchiveAircraft(
      { id: unarchiveTarget.aircraftId },
      {
        onSuccess: () => {
          toast.success('Aircraft unarchived')
          setUnarchiveTarget(null)
          onClose()
        },
        onError: () => {
          toast.error('Failed to unarchive aircraft')
          setUnarchiveTarget(null)
        },
      }
    )
  }

  useEffect(() => {
    if (isOpen) {
      if (aircraftId && aircraftData) {
        // Edit mode - populate form with aircraft data
        const formData: AircraftData = {
          registration: aircraftData.registration,
          type: aircraftData.type,
          baseAirportIcao: aircraftData.baseAirportIcao,
          maxPassengers: aircraftData.maxPassengers,
          crewCapacity: aircraftData.crewCapacity,
          isDefault: aircraftData.isDefault,
          cateringGalley: aircraftData.cateringGalley ?? '',
          cabinComfort: aircraftData.cabinComfort ?? '',
          entertainmentConnectivity: aircraftData.entertainmentConnectivity ?? '',
          safetyEquipment: aircraftData.safetyEquipment ?? '',
          storageSupplies: aircraftData.storageSupplies ?? '',
          facilityNotes: aircraftData.facilityNotes ?? '',
          medicalInfo: aircraftData.medicalInfo ?? '',
        }
        reset(formData)
      } else {
        // Create mode - reset to empty form
        reset(defaultFormData)
      }
    }
  }, [isOpen, aircraftId, aircraftData, reset])

  const handleAirportSelect = (icaoCode: string) => {
    setValue('baseAirportIcao', icaoCode, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    })
    setIsAirportPickerOpen(false)
  }

  const handleFormSubmit = handleSubmit(async (data) => {
    const facilityValues = {
      cateringGalley: data.cateringGalley,
      cabinComfort: data.cabinComfort,
      entertainmentConnectivity: data.entertainmentConnectivity,
      safetyEquipment: data.safetyEquipment,
      storageSupplies: data.storageSupplies,
      facilityNotes: data.facilityNotes,
      medicalInfo: data.medicalInfo,
    }

    try {
      if (isEditMode && aircraftId) {
        await updateAircraft({
          id: aircraftId,
          registration: data.registration,
          type: data.type,
          baseAirportIcao: data.baseAirportIcao,
          maxPassengers: data.maxPassengers,
          crewCapacity: data.crewCapacity,
          isDefault: data.isDefault,
          ...facilityValues,
        })
        // Save staged facility photos against the existing aircraft.
        await flushFacilityPhotos(aircraftId)
        toast.success('Aircraft updated successfully')
      } else {
        const created = await createAircraft({
          registration: data.registration,
          type: data.type,
          baseAirportIcao: data.baseAirportIcao,
          maxPassengers: data.maxPassengers,
          crewCapacity: data.crewCapacity,
          isDefault: data.isDefault,
          ...facilityValues,
        })
        // Attach staged photos to the freshly created aircraft's id.
        await flushFacilityPhotos((created as { id: string }).id)
        track(EVENTS.AIRCRAFT_CREATED)
        toast.success('Aircraft added successfully')
      }
      onClose()
    } catch (error) {
      if (handleConflict(data.registration, error)) return
      console.error('Error saving aircraft:', error)
      toast.error(
        isEditMode ? 'Failed to update aircraft' : 'Failed to add aircraft'
      )
    }
  })

  return (
    <>
      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleRequestClose()
        }}
      >
        <DrawerContent className="bg-surface-L0 shadow-lg max-h-[85dvh]">
          {/* Header */}
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="font-primary font-semibold text-lg text-content-primary-bold">
              {isEditMode ? 'Edit Aircraft' : 'Add Aircraft'}
            </DrawerTitle>
            <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleRequestClose} disabled={isSaving}>
              <X className="w-4 h-4" />
            </Button>
          </DrawerHeader>

          {/* Form Content */}
          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-0"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <form className="flex flex-col gap-4 p-2">
              <PrntrCard title="Aircraft">
                <TextRow
                  control={control}
                  name="registration"
                  label={
                    <>
                      Tail Number <span className="text-content-negative-bold">*</span>
                    </>
                  }
                  iconLeft={<Plane />}
                  placeholder="e.g. N9873"
                  nullable={false}
                  error={errors.registration?.message}
                />
                <TextRow
                  control={control}
                  name="type"
                  label="Type"
                  placeholder="e.g. Gulfstream G650"
                  nullable={false}
                  error={errors.type?.message}
                />
                <PickerRowField
                  control={control}
                  name="baseAirportIcao"
                  label={
                    <>
                      Base <span className="text-content-negative-bold">*</span>
                    </>
                  }
                  display={(value) =>
                    value && airport
                      ? `${airport.icaoCode}${airport.municipality ? ` · ${airport.municipality}` : ''}`
                      : null
                  }
                  placeholder="Select airport"
                  onOpen={() => setIsAirportPickerOpen(true)}
                  error={errors.baseAirportIcao?.message}
                />
                <NumRow
                  control={control}
                  name="maxPassengers"
                  label="Max Passengers"
                  integer
                  error={errors.maxPassengers?.message}
                />
                <NumRow
                  control={control}
                  name="crewCapacity"
                  label="Crew Capacity"
                  integer
                  error={errors.crewCapacity?.message}
                />
                <SwitchRowField
                  control={control}
                  name="isDefault"
                  label={
                    <span className="flex flex-col">
                      Make Default
                      <span className="text-xs font-normal text-content-primary-subtle">
                        This aircraft will be the default for flights.
                      </span>
                    </span>
                  }
                />
              </PrntrCard>

              {/* Preferences / Facilities */}
              <div className="flex flex-col gap-4 pt-2">
                <h3 className="font-primary font-semibold text-base text-content-primary-bold tracking-[-0.5px] leading-6">
                  Preferences
                </h3>
                {FACILITY_FIELDS.map(({ name, label }) => (
                  <div key={name} className="flex flex-col gap-2">
                    <TextareaField
                      control={control}
                      name={name}
                      label={label}
                      error={errors[name]?.message}
                    />
                    <FacilityPhotos
                      key={aircraftId ?? 'new'}
                      ref={(el) => {
                        photoRefs.current[name] = el
                      }}
                      facility={name}
                      aircraftId={aircraftId}
                      s3Keys={photosByFacility[name] ?? []}
                      trigger="icon"
                      onPendingChange={(count) =>
                        setPendingPhotoCounts((prev) =>
                          prev[name] === count
                            ? prev
                            : { ...prev, [name]: count }
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            </form>
          </div>

          {/* Footer */}
          <DrawerFooter className="flex flex-row gap-2 items-start justify-end p-4">
            <Button
              onClick={handleRequestClose}
              variant="secondary"
              size="mini"
              disabled={isSaving}
              className="flex-1 font-primary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleFormSubmit}
              variant="primary"
              size="mini"
              disabled={isSaving || (!isDirty && pendingPhotoTotal === 0) || !isValid}
              className="flex-1 font-primary"
              loading={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Airport Picker Drawer */}
      <AirportPickerDrawer
        isOpen={isAirportPickerOpen}
        onClose={() => setIsAirportPickerOpen(false)}
        onSelect={handleAirportSelect}
        searchParam={watchBaseAirportIcao}
      />

      {/* Unarchive archived aircraft confirmation */}
      <AlertDialog
        open={!!unarchiveTarget}
        onOpenChange={(open) => {
          if (!open) setUnarchiveTarget(null)
        }}
      >
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>Unarchive Archived Aircraft?</AlertDialogTitle>
            <AlertDialogDescription>
              An aircraft with the tail number{' '}
              <span className="font-medium text-content-primary-bold">
                {unarchiveTarget?.registration}
              </span>{' '}
              was previously archived. Unarchive it instead of creating a new one?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnarchiveConfirm}>
              Unarchive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
